// M11 Standorte: Liste, Anlegen/Bearbeiten (Name, Adresse, Zeitzone, Koordinaten), Löschen, Inhalt je Standort.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../api/client';
import { can, qk, useAssignments, useDevices, useLocations, useMe, usePlaylists, useSchedules } from '../api/queries';
import type { Location } from '../api/types';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Empty, Field, PageHead, Spinner } from '../components/ui';
import { AssignDialog } from './AssignDialog';

export const timeZones = (): string[] => {
  const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  return f ? f('timeZone') : ['Europe/Zurich', 'Europe/Berlin', 'Europe/Vienna', 'Europe/London', 'UTC'];
};

export function LocationsPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: locations, isLoading } = useLocations();
  const { data: devices = [] } = useDevices();
  const { data: assignments = [] } = useAssignments();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState<Location | 'new' | null>(null);
  const [del, setDel] = useState<Location | null>(null);
  const [assign, setAssign] = useState<Location | null>(null);
  const manage = can(me, 'locations.manage');

  return (
    <>
      <PageHead title={t('nav.locations')} sub={t('locations.sub')}
        actions={manage && <button className="btn primary" onClick={() => setEdit('new')}><Icon name="plus" />{t('locations.new')}</button>} />
      {isLoading ? <Spinner /> : !locations?.length ? (
        <div className="card"><Empty icon="pin" title={t('locations.empty')} /></div>
      ) : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr><th>{t('common.name')}</th><th>{t('locations.address')}</th><th>{t('locations.timezone')}</th><th>{t('nav.screens')}</th><th>{t('locations.content')}</th><th /></tr></thead>
            <tbody>
              {[...locations].sort((a, b) => a.name.localeCompare(b.name)).map((l) => {
                const n = devices.filter((d) => d.locationId === l.id && d.status === 'active').length;
                const a = assignments.find((x) => x.targetType === 'location' && x.targetId === l.id);
                const content = a ? (a.playlistId ? playlists.find((p) => p.id === a.playlistId)?.name : schedules.find((s) => s.id === a.scheduleId)?.name) : null;
                return (
                  <tr key={l.id}>
                    <td><b>{l.name}</b></td>
                    <td className="muted">{l.address ?? '–'}</td>
                    <td className="muted">{l.timezone}</td>
                    <td>{n}</td>
                    <td className="muted">{content ?? t('locations.inherited')}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {can(me, 'assignments.manage') && <button className="btn sm ghost" onClick={() => setAssign(l)}>{t('assign.title')}</button>}
                      {manage && <button className="btn sm ghost icon" aria-label={t('common.edit')} title={t('common.edit')} onClick={() => setEdit(l)}><Icon name="edit" /></button>}
                      {manage && <button className="btn sm ghost icon danger" aria-label={t('common.delete')} title={t('common.delete')} onClick={() => setDel(l)}><Icon name="trash" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit && <LocationDialog location={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
      {assign && <AssignDialog targets={[{ targetType: 'location', targetId: assign.id }]} title={`${t('assign.title')}: ${assign.name}`} onClose={() => setAssign(null)} />}
      {del && (
        <Confirm danger title={t('common.delete')} text={t('locations.confirmDelete', { name: del.name })} confirmLabel={t('common.delete')} onClose={() => setDel(null)}
          onConfirm={async () => {
            try {
              await api.del(`/locations/${del.id}`, { ifMatch: del.rowVersion });
              await qc.invalidateQueries({ queryKey: qk.locations });
            } catch (e) {
              if (e instanceof ApiError && e.status === 409) toast.show(t('locations.hasDevices'));
              else toast.error(e);
            }
            setDel(null);
          }} />
      )}
    </>
  );
}

function LocationDialog({ location, onClose }: { location: Location | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(location?.name ?? '');
  const [address, setAddress] = useState(location?.address ?? '');
  const [timezone, setTimezone] = useState(location?.timezone ?? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich'));
  const [lat, setLat] = useState(location?.latitude?.toString() ?? '');
  const [lon, setLon] = useState(location?.longitude?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const body = { name: name.trim(), address: address.trim() || null, timezone, latitude: lat ? Number(lat) : null, longitude: lon ? Number(lon) : null };
    try {
      if (location) await api.patch(`/locations/${location.id}`, body, { ifMatch: location.rowVersion });
      else await api.post('/locations', body);
      await qc.invalidateQueries({ queryKey: qk.locations });
      toast.show(t('common.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={location ? location.name : t('locations.new')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim() || busy} onClick={submit}>{t('common.save')}</button>
    </>}>
      <form className="col" onSubmit={(e) => { e.preventDefault(); if (name.trim()) void submit(); }}>
        <Field label={t('common.name')}><input autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('locations.namePh')} /></Field>
        <Field label={t('locations.address')}><input value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
        <Field label={t('locations.timezone')} hint={t('locations.timezoneHint')}>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>{timeZones().map((z) => <option key={z}>{z}</option>)}</select>
        </Field>
        <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label={t('studio.f.lat')}><input type="number" step="0.0001" min={-90} max={90} value={lat} onChange={(e) => setLat(e.target.value)} /></Field>
          <Field label={t('studio.f.lon')}><input type="number" step="0.0001" min={-180} max={180} value={lon} onChange={(e) => setLon(e.target.value)} /></Field>
        </div>
      </form>
    </Dialog>
  );
}
